import { useMemo } from 'react'

import { IconTrash } from '@tabler/icons-react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import type { Asset } from 'apiTypes/Asset'
import { VehicleOwnershipStatus } from 'apiTypes/VehicleOwnershipStatus'
import { VehicleType } from 'apiTypes/VehicleType'

import { ButtonIcon } from 'components/Buttons'
import { Input, Select } from 'components/FormFields'

import { Header } from './Header'

type Model = {
	assets: Array<Asset>
}

type Props = {
	index: number
	isReadOnly: boolean
	onRemove: () => void
}

export const VehicleForm = ({ index, isReadOnly, onRemove }: Props) => {
	const { control } = useFormContext<Model>()
	const { formatMessage } = useIntl()
	const vehicleType = useWatch({
		control,
		name: `assets.${index}.vehicleType`,
	})
	const vehicleTypeOptions = useMemo(
		() => [
			{
				value: VehicleType.Car,
				label: formatMessage({ id: 'flow.assets.vehicleType.car', defaultMessage: 'Car' }),
			},
			{
				value: VehicleType.Motorcycle,
				label: formatMessage({ id: 'flow.assets.vehicleType.motorcycle', defaultMessage: 'Motorcycle' }),
			},
			{
				value: VehicleType.Boat,
				label: formatMessage({ id: 'flow.assets.vehicleType.boat', defaultMessage: 'Boat' }),
			},
		],
		[formatMessage],
	)
	const ownershipOptions = useMemo(
		() => [
			{
				value: VehicleOwnershipStatus.Owner,
				label: formatMessage({ id: 'flow.assets.ownership.owner', defaultMessage: 'Owner' }),
			},
			{
				value: VehicleOwnershipStatus.Leasing,
				label: formatMessage({ id: 'flow.assets.ownership.leasing', defaultMessage: 'Leasing' }),
			},
		],
		[formatMessage],
	)
	const isLicensePlateRequired = vehicleType !== VehicleType.Boat

	return (
		<section>
			<Header>
				<h2>
					<FormattedMessage
						id='flow.assets.vehicleTitle'
						defaultMessage='Asset {index}: Vehicle'
						values={{ index: index + 1 }}
					/>
				</h2>
				{!isReadOnly && (
					<ButtonIcon
						aria-label={formatMessage({
							id: 'flow.assets.buttonRemoveVehicle',
							defaultMessage: 'Remove vehicle',
						})}
						onClick={onRemove}
						size={22}
						renderIcon={(props) => <IconTrash {...props} strokeWidth={1.8} />}
					/>
				)}
			</Header>
			<Select
				name={`assets.${index}.vehicleType`}
				label={<FormattedMessage id='flow.assets.labelVehicleType' defaultMessage='Select vehicle' />}
				isReadOnly={isReadOnly}
				isClearable={false}
				options={vehicleTypeOptions}
				isRequired
			/>
			<Select
				name={`assets.${index}.ownershipStatus`}
				label={<FormattedMessage id='flow.assets.labelOwnershipStatus' defaultMessage='Ownership status' />}
				isReadOnly={isReadOnly}
				isClearable={false}
				options={ownershipOptions}
				isRequired
			/>
			<Input
				name={`assets.${index}.licensePlate`}
				label={<FormattedMessage id='flow.assets.labelLicensePlate' defaultMessage='License plate' />}
				isReadOnly={isReadOnly}
				isRequired={isLicensePlateRequired}
			/>
			<Input
				name={`assets.${index}.comment`}
				label={<FormattedMessage id='flow.assets.labelComment' defaultMessage='Comment' />}
				type='multiline'
			/>
		</section>
	)
}
